'use client'

import { useState, useEffect } from 'react'
import { useUser } from '@/hooks/useUser'
import { useToast } from '@/hooks/useToast'
import { Review, Property } from '@/types'
import { getApiUrl } from '@/lib/apiUrl'

interface ReviewWithDetails extends Review {
  property?: Property
  reviewer?: {
    firstName?: string
    lastName?: string
    email?: string
  }
  landlord?: {
    firstName?: string
    lastName?: string
    email?: string
  }
}

export default function ReviewsManagement() {
  const { token } = useUser()
  const toast = useToast()
  const [reviews, setReviews] = useState<ReviewWithDetails[]>([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState<'all' | 'pending' | 'approved' | 'reported'>('all')
  const [searchQuery, setSearchQuery] = useState('')

  useEffect(() => {
    loadReviews()
  }, [])

  const loadReviews = async () => {
    if (!token) {
      setLoading(false)
      return
    }

    setLoading(true)
    try {
      const apiUrl = getApiUrl()
      const response = await fetch(`${apiUrl}/reviews`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (response.ok) {
        const data = await response.json()
        setReviews(data.data || data || [])
      }
    } catch (error) {
      console.error('Error loading reviews:', error)
      toast.error('Failed to load reviews')
    } finally {
      setLoading(false)
    }
  }

  const handleModerateReview = async (reviewId: string, action: 'approve' | 'reject' | 'delete') => {
    if (!token) {
      toast.error('Please log in to moderate reviews')
      return
    }

    try {
      const apiUrl = getApiUrl()
      const response = await fetch(`${apiUrl}/reviews/${reviewId}/moderate`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ action })
      })

      if (!response.ok) {
        const data = await response.json()
        throw new Error(data.message || 'Failed to moderate review')
      }

      toast.success(`Review ${action}d successfully`)
      loadReviews()
    } catch (error) {
      console.error('Error moderating review:', error)
      toast.error(error instanceof Error ? error.message : 'Failed to moderate review')
    }
  }

  const getRatingStars = (rating: number) => {
    return '⭐'.repeat(rating) + '☆'.repeat(5 - rating)
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Unknown date'
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  const filteredReviews = reviews.filter(review => {
    // Filter by status (assuming reviews have a status field, defaulting to 'approved')
    const reviewStatus = (review as any).status || 'approved'
    if (filter === 'pending' && reviewStatus !== 'pending') return false
    if (filter === 'approved' && reviewStatus !== 'approved') return false
    if (filter === 'reported' && reviewStatus !== 'reported') return false

    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase()
      const propertyTitle = review.property?.title?.toLowerCase() || ''
      const reviewerName = review.reviewer?.email?.toLowerCase() || ''
      const reviewText = (review.comment || (review as any).review_text || '').toLowerCase()
      const reviewTitle = review.title?.toLowerCase() || ''
      
      if (!propertyTitle.includes(query) && 
          !reviewerName.includes(query) && 
          !reviewText.includes(query) && 
          !reviewTitle.includes(query)) {
        return false
      }
    }

    return true
  })

  // Calculate stats
  const stats = {
    total: reviews.length,
    averageRating: reviews.length > 0
      ? reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length
      : 0,
    pending: reviews.filter(r => ((r as any).status || 'approved') === 'pending').length,
    reported: reviews.filter(r => ((r as any).status || 'approved') === 'reported').length
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-6 bg-gray-200 rounded w-1/3"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            <div className="space-y-3">
              <div className="h-20 bg-gray-200 rounded"></div>
              <div className="h-20 bg-gray-200 rounded"></div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-display text-stone-900">Reviews Management</h2>
          <p className="text-stone-600 mt-1">Moderate and manage all property reviews</p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-6">
          <h3 className="text-lg font-medium text-stone-900 mb-2">Total Reviews</h3>
          <p className="text-3xl font-display text-emerald-600">{stats.total}</p>
          <p className="text-sm text-stone-500">All reviews</p>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-6">
          <h3 className="text-lg font-medium text-stone-900 mb-2">Average Rating</h3>
          <p className="text-3xl font-display text-amber-600">{stats.averageRating.toFixed(1)}</p>
          <p className="text-sm text-stone-500">{getRatingStars(Math.round(stats.averageRating))}</p>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-6">
          <h3 className="text-lg font-medium text-stone-900 mb-2">Pending Review</h3>
          <p className="text-3xl font-display text-yellow-600">{stats.pending}</p>
          <p className="text-sm text-stone-500">Awaiting moderation</p>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-6">
          <h3 className="text-lg font-medium text-stone-900 mb-2">Reported</h3>
          <p className="text-3xl font-display text-red-600">{stats.reported}</p>
          <p className="text-sm text-stone-500">Flagged reviews</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-stone-700 mb-2">
              Search Reviews
            </label>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by property, reviewer, or review content..."
              className="w-full px-4 py-2 border border-stone-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white text-stone-900"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-2">
              Filter by Status
            </label>
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value as any)}
              className="px-4 py-2 border border-stone-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white text-stone-900"
            >
              <option value="all">All Reviews</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="reported">Reported</option>
            </select>
          </div>
        </div>
      </div>

      {/* Reviews List */}
      <div className="bg-white rounded-2xl shadow-sm border border-stone-200">
        <div className="px-6 py-4 border-b border-stone-200">
          <h3 className="text-lg font-medium text-stone-900">
            {filteredReviews.length} Review{filteredReviews.length !== 1 ? 's' : ''}
          </h3>
        </div>

        {filteredReviews.length > 0 ? (
          <div className="divide-y divide-stone-200">
            {filteredReviews.map((review) => {
              const reviewId = (review as any)._id || review.id
              const reviewer = review.reviewer
              const reviewerName = reviewer?.firstName && reviewer?.lastName
                ? `${reviewer.firstName} ${reviewer.lastName}`
                : reviewer?.email?.split('@')[0] || 'Anonymous'
              const isAnonymous = (review as any).isAnonymous || (review as any).is_anonymous
              const propertyTitle = review.property?.title || 'Unknown Property'
              const reviewStatus = (review as any).status || 'approved'

              return (
                <div key={reviewId} className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h4 className="font-semibold text-stone-900">{review.title}</h4>
                        <div className="flex text-yellow-400 text-sm">{getRatingStars(review.rating)}</div>
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                          reviewStatus === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                          reviewStatus === 'reported' ? 'bg-red-100 text-red-800' :
                          'bg-emerald-100 text-emerald-800'
                        }`}>
                          {reviewStatus.charAt(0).toUpperCase() + reviewStatus.slice(1)}
                        </span>
                      </div>

                      <div className="text-sm text-stone-600 space-y-1 mb-3">
                        <p>
                          <span className="font-medium">Property:</span> {propertyTitle}
                        </p>
                        <p>
                          <span className="font-medium">Reviewer:</span>{' '}
                          {isAnonymous ? 'Anonymous' : reviewerName}
                        </p>
                        <p>
                          <span className="font-medium">Date:</span>{' '}
                          {formatDate((review as any).createdAt || (review as any).created_at)}
                        </p>
                      </div>

                      <p className="text-stone-700 leading-relaxed mb-4">
                        {review.comment || (review as any).review_text}
                      </p>

                      {/* Landlord Response */}
                      {(review as any).landlordResponse && (
                        <div className="mt-4 pl-4 border-l-4 border-emerald-200 bg-emerald-50 rounded-xl p-4">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-sm font-semibold text-emerald-900">Landlord Response</span>
                          </div>
                          <p className="text-sm text-emerald-800">
                            {(review as any).landlordResponse.comment}
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Admin Actions */}
                    <div className="ml-4 flex flex-col gap-2">
                      {reviewStatus === 'pending' && (
                        <>
                          <button
                            onClick={() => handleModerateReview(reviewId, 'approve')}
                            className="px-4 py-2 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 transition-colors text-sm font-semibold"
                          >
                            Approve
                          </button>
                          <button
                            onClick={() => handleModerateReview(reviewId, 'reject')}
                            className="px-4 py-2 bg-amber-600 text-white rounded-xl hover:bg-amber-700 transition-colors text-sm font-semibold"
                          >
                            Reject
                          </button>
                        </>
                      )}
                      {reviewStatus === 'reported' && (
                        <button
                          onClick={() => handleModerateReview(reviewId, 'delete')}
                          className="px-4 py-2 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors text-sm font-semibold"
                        >
                          Delete
                        </button>
                      )}
                      {(reviewStatus === 'approved' || reviewStatus === 'pending') && (
                        <button
                          onClick={() => handleModerateReview(reviewId, 'delete')}
                          className="px-4 py-2 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors text-sm font-semibold"
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        ) : (
          <div className="p-12 text-center text-stone-500">
            <div className="text-5xl mb-4">⭐</div>
            <p className="text-lg font-medium mb-2">No reviews found</p>
            <p className="text-sm">Try adjusting your filters or search query.</p>
          </div>
        )}
      </div>
    </div>
  )
}

